import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getPreferredLanguage } from "@/lib/utils";
import { sendMagicLinkApiV1LoginSendMagicLinkPost } from "../client/index";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const loginFormSchema = z.object({
  email: z.string().min(2, {
    message: "Email must be at least 2 characters.",
  }),
  language: z.string().default("nl"),
});

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    mutateAsync: sendMagicLink,
    isPending: isSending,
    error,
  } = useMutation({
    mutationFn: sendMagicLinkApiV1LoginSendMagicLinkPost,
    onSuccess: () => {
      navigate({ to: "/verify-request", replace: true });
    },
  });

  const form = useForm<z.input<typeof loginFormSchema>, unknown, z.infer<typeof loginFormSchema>>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: "",
      language: getPreferredLanguage(),
    },
  });

  async function onSubmit(values: z.infer<typeof loginFormSchema>) {
    await sendMagicLink({
      requestBody: {
        email: values.email,
        language: ["en", "nl"].includes(values.language) ? (values.language as "en" | "nl") : "nl",
      },
    });
  }

  return (
    <div className="flex min-h-[80vh] flex-col items-center justify-center p-4">
      <Card className="flex flex-col items-center justify-center gap-2 p-8 shadow-lg">
        <CardHeader className="text-2xl font-semibold tracking-tight">{t("Welcome back")}</CardHeader>

        <p className="text-sm text-muted-foreground">{t("Enter your email to sign in to your account")}</p>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="rounded-lgp-4 flex h-full w-full min-w-[300px] max-w-sm flex-col gap-4 md:min-w-[500px]"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("Email")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("john@example.com")} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(
                      "A magic link, which allows you to log in securely without a password, will be sent to the email address you provide.",
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isSending}>
              {isSending ? <Loader2 className="animate-spin" /> : t("Sign In with Email")}
            </Button>
            {error && <p className="text-center text-red-500">{t(error.message)}</p>}
          </form>
        </Form>
      </Card>
    </div>
  );
}
